import styled from '@emotion/styled';
import { css } from '@emotion/react';

import { Heading1, Text } from '@blueprint/ui';
import { ContentfulImageWrapper } from '../../queries/heroContent.types';

const MOBILE_IMAGE_HEIGHT = 150;
const MOBILE_SEARCH_INPUT_TITLE_MARGIN = 12;
const TABLET_IMAGE_HEIGHT = 286;
const TABLET_CONTENT_WRAPPER_HEIGHT = 216;
const DESKTOP_IMAGE_HEIGHT = 450;

type Props = {
    src: ContentfulImageWrapper['url'];
    width: number;
    height: number;
    focalPoint: ContentfulImageWrapper['focalPoint'] | undefined;
};

const getBackgroundPosition = (xy: number, focalPoint: number): number => {
    const offset = (100 / xy) * focalPoint;
    const result = 100 - offset;

    return result;
};

export const SearchInitiationWrapper = styled.div(
    ({ theme: { spacing, borderRadius, mq } }) => css`
        position: relative;
        display: flex;
        flex-direction: column;
        width: 100%;
        border-radius: ${borderRadius['300']};

        ${mq.medium} {
            margin-bottom: -${spacing['64px']};
            margin-top: ${spacing['8px']};
            padding: 0;
        }

        ${mq.large} {
            height: ${TABLET_IMAGE_HEIGHT + TABLET_CONTENT_WRAPPER_HEIGHT}px;
            margin-bottom: ${spacing['104px']};
            border-radius: ${borderRadius['600']};
        }
    `
);

// All positioning here is currently done for a very specific image, so
// this will not 'just work' if another hero image is uploaded to Contentful
export const HeroImage = styled('div')<Props>(
    ({ theme: { borderRadius, mq }, src, width, height, focalPoint }) => {
        const backgroundPositionY = focalPoint?.y
            ? getBackgroundPosition(height, focalPoint?.y)
            : 100;

        const backgroundPositionX = focalPoint?.x
            ? getBackgroundPosition(width, focalPoint?.x)
            : 100;

        return css`
            z-index: -1;
            width: 100%;
            max-width: 100%;
            height: ${MOBILE_IMAGE_HEIGHT}px;
            background-image: url(${src}?fm=jpg&w=960&q=85&fl=progressive);
            background-position: ${backgroundPositionY + 20}%
                ${backgroundPositionX}%;
            background-repeat: no-repeat;
            background-size: cover;

            @media only screen and (min-width: 380px) {
                background-position: ${backgroundPositionY}%
                    ${backgroundPositionX}%;
            }

            ${mq.small} {
                background-position: ${backgroundPositionY}%
                    ${backgroundPositionX + 10}%;
            }

            ${mq.medium} {
                height: ${TABLET_IMAGE_HEIGHT}px;
                border-radius: ${borderRadius['500']};
            }

            ${mq.large} {
                ${
                    // TODO: When Chrome/Safari support image-set() with type() usage
                    // change this logic so webp is served and update the preload image in HeroContainer.tsx
                    // to preload the webp version
                    //
                    // @supports (background: image-set("any-image.jpg" type("image/webp")) ){
                    //     background-image: image-set(
                    //         url(${src}?fm=webp&w=${width}&h=${height}&q=75) type("image/webp")
                    //     );
                    // }
                    false
                }

                position: absolute;
                top: 0;
                height: ${DESKTOP_IMAGE_HEIGHT}px;
                background-image: url(${src}?fm=jpg&w=${width}&h=${height}&q=85&fl=progressive);
                background-position: ${backgroundPositionY}%
                    ${backgroundPositionX - 5}%;
                background-size: auto;
                margin-top: 130px;
                width: 104%;
                max-width: 104%;
                left: -2%;
            }

            ${mq.extraLarge} {
                max-width: 1312px;
            }

            @media only screen and (min-width: 1332px) {
                width: 110%;
                left: -5%;
            }
        `;
    }
);

// TODO: alias box shadow to in-page-low
export const MobileHeroContentWrapper = styled('div')(
    ({ theme: { boxShadow, mq, spacing, borderRadius, colors } }) => css`
        width: 100%;
        display: flex;
        flex-direction: column;
        border-radius: ${borderRadius['500']};
        padding: ${spacing['16px']};
        background-color: ${colors['graphic-background']};
        margin-top: -${spacing['16px']};
        margin-bottom: ${spacing['32px']};
        width: 100%;
        box-shadow: ${boxShadow['100']};
        align-self: center;

        ${mq.medium} {
            width: 90%;
            padding: ${spacing['16px']} ${spacing['32px']};
        }

        ${mq.large} {
            display: none;
        }
    `
);

export const DesktopSearchInputWrapper = styled('div')(
    ({ theme: { mq } }) => css`
        display: none;

        ${mq.large} {
            display: flex;
            width: 100%;
            flex-direction: column;
            max-width: 983px;
        }
    `
);

export const SearchBarWrapper = styled.div(
    ({ theme: { spacing } }) => css`
        margin-top: ${spacing['24px']};
    `
);

export const SearchInitiation = styled.div(
    ({ theme: { colors, borderRadius } }) => css`
        display: flex;
        width: 100%;
        background: ${colors.white};
        border-radius: ${borderRadius['300']};
        height: 72px;
    `
);

export const MobileMainHeadline = styled(Text)(
    ({ theme: { mq, fontSizes, type } }) => css`
        font-family: 'ZooplaDisplay';
        font-weight: ${type['semiBold']};
        display: flex;
        align-self: flex-start;
        font-size: ${fontSizes['6']};
        line-height: ${fontSizes['5']};

        ${mq.medium} {
            font-size: ${fontSizes['5']};
        }

        ${mq.large} {
            display: none;
        }
    `
);

export const DesktopMainHeadline = styled(Text)(
    ({ theme: { mq, spacing, colors, type } }) => css`
        display: none;

        ${mq.large} {
            font-family: 'ZooplaDisplay';
            font-weight: ${type['semiBold']};
            color: ${colors.white};
            margin-top: ${spacing['32px']};
            font-size: 36px;
            display: flex;
        }
    `
);

export const MobileSearchInputTitle = styled(Text)(
    ({ theme: { mq, fontSizes, spacing } }) => {
        return css`
            font-size: ${fontSizes[2]};
            line-height: ${spacing['24px']};
            margin-top: ${MOBILE_SEARCH_INPUT_TITLE_MARGIN}px;
            margin-bottom: ${MOBILE_SEARCH_INPUT_TITLE_MARGIN}px;

            ${mq.small} {
                margin-top: ${spacing['16px']};
                margin-bottom: ${spacing['24px']};
            }
        `;
    }
);

export const DesktopSearchInputTitle = styled(Heading1)(
    ({ theme: { fontSizes, spacing, type } }) => css`
        font-size: ${fontSizes[2]};
        font-weight: ${type.normal};
        margin-top: ${spacing['8px']};
        margin-bottom: ${spacing['8px']};
        display: flex;
        align-self: flex-start;
    `
);
